import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Container } from '@fiverr-private/layout_components';
import { getPortfolioModalProjectDataQueryKey } from '../../../server/queries';
import { usePortfolioWidgetContext } from '../PortfolioWidgetContext';
import { PortfolioProjectCard } from '../../PortfolioProjectCard';
import { usePortfolioModalProjectDataQuery, usePortfolioWidgetFullDataQuery } from '../../../queries';
import { Modal } from '../Modal';
import { selectPortfolioProject } from '../../../utils';
import { useBiClickOnThumbnail, useBiComponentEnteredView, useBiComponentExists } from '../PortfolioWidget.bi';
import { PortfolioAttachmentTransformation } from '../../../gql-operations/gql-generated';
import { Layout, LayoutProps, LayoutThumbnails, Thumbnail, ThumbnailImage, ThumbnailSkeleton } from './Layout';

export interface WidgetProps extends LayoutProps {
  /**
   * Will render fallback if there are no projects to show.
   */
  fallback?: ReactNode;
}

export const Widget: FC<WidgetProps> = ({ cardSize, thumbnailsPosition, thumbnailsAmount, fallback }) => {
  const client = useQueryClient();

  const {
    biEnrichment,
    username,
    roleIds,
    roles,
    projectSource,
    servicesConfig: { portfolioProjectDataUrlBuilder, portfolioProjectsDataUrlBuilder },
  } = usePortfolioWidgetContext();

  const { data: widgetData } = usePortfolioWidgetFullDataQuery({
    username,
    roleIds,
    roles,
    projectSource,
    urlBuilder: portfolioProjectsDataUrlBuilder,
  });

  const [currentProjectId, setCurrentProjectId] = useState(widgetData?.firstProject.id);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (widgetData) {
      client.setQueryData(
        getPortfolioModalProjectDataQueryKey({ username, projectId: widgetData.firstProject.id }),
        widgetData.firstProject
      );

      setCurrentProjectId(widgetData.projects[0].id);
    }
  }, [widgetData, username, client]);

  const { data: projectData } = usePortfolioModalProjectDataQuery({
    username,
    projectId: currentProjectId,
    cardTransformation: PortfolioAttachmentTransformation.TPortfolioProjectCard,
    urlBuilder: portfolioProjectDataUrlBuilder,
  });

  useBiComponentExists(widgetData);

  useBiComponentEnteredView(containerRef);

  const biOnClick = useBiClickOnThumbnail();

  // This is important to check if data exists, as we want to render nothing if there are no projects
  // otherwise we will render a skeleton to indicate loading process.
  if (widgetData && widgetData.projects.length === 0) {
    return <>{fallback || null}</>;
  }

  return (
    <Modal projectIds={widgetData?.projects.map(({ id }) => id) || []} biEnrichment={biEnrichment.current}>
      {({ openPortfolioModal }) => (
        <Container ref={containerRef}>
          {widgetData && widgetData.projects.length < 2 ? (
            <PortfolioProjectCard
              biEnrichment={biEnrichment.current}
              size={cardSize}
              project={projectData ? selectPortfolioProject(projectData) : undefined}
              onClick={projectData ? () => openPortfolioModal(projectData.id) : undefined}
            />
          ) : (
            <Layout thumbnailsPosition={thumbnailsPosition} thumbnailsAmount={thumbnailsAmount} cardSize={cardSize}>
              <PortfolioProjectCard
                biEnrichment={biEnrichment.current}
                size={cardSize}
                project={projectData ? selectPortfolioProject(projectData) : undefined}
                onClick={projectData ? () => openPortfolioModal(projectData.id) : undefined}
              />
              <LayoutThumbnails amount={widgetData?.projects.length}>
                {widgetData
                  ? widgetData.projects.map(({ id, items, title, source }, index) => (
                      <Thumbnail
                        index={index}
                        onClick={() => {
                          setCurrentProjectId(id);
                          biOnClick({ projectId: id, projectSource: source });
                        }}
                        isActive={id === currentProjectId}
                        key={id}
                        label={title || undefined}
                      >
                        <ThumbnailImage src={items?.nodes?.[0].attachment?.previewUrl || undefined} alt={title || ''} />
                      </Thumbnail>
                    ))
                  : [...Array(2).keys()].map((index) => <ThumbnailSkeleton index={index} key={index} />)}
              </LayoutThumbnails>
            </Layout>
          )}
        </Container>
      )}
    </Modal>
  );
};
